/**
 * 玩家回放 - 场次元数据卡片
 *
 * 显示单个 (world_id, player_id) 会话的摘要信息。
 *
 * 字段数据源 (CHANGED 2026-06-19):
 *   - world_id / player_id        — 实时查询 (DB 主键)
 *   - status / started_at / ended_at — portal.world_instances_sync (LEFT JOIN, 真实)
 *   - character_name / player_name / score / rank / ending_type / ending_name
 *                               — player_summary_report.record_data.report_snapshot
 *                                 顶层字段 (JSONB 扁平读取, 无需 JOIN player_characters_sync)
 *   - 字段为 null 时, 仍走 "暂无" 占位 (云端兜底, ai-engine 未上报或字段缺失场景)
 */

import React from 'react'
import { Card, Descriptions, Tag, Empty, Skeleton, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import type { PlaybackSessionSummary } from '@/types/playback'

interface Props {
  session: PlaybackSessionSummary | null
  loading?: boolean
}

const STATUS_COLOR: Record<string, string> = {
  running: 'processing',
  ended: 'default',
  settling: 'warning',
  settled: 'success',
  failed: 'error',
}

const ENDING_COLOR: Record<string, string> = {
  perfect: 'gold',
  good: 'green',
  normal: 'blue',
  bad: 'orange',
  tragic: 'red',
}

const NA = (
  <span style={{ color: '#bfbfbf' }}>
    暂无
    <Tooltip title="云端暂无此数据源, 后续 plan 接入">
      <InfoCircleOutlined style={{ marginLeft: 4, fontSize: 11 }} />
    </Tooltip>
  </span>
)

const SessionMetadata: React.FC<Props> = ({ session, loading }) => {
  if (loading) {
    return (
      <Card title="会话元数据">
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    )
  }
  if (!session) {
    return (
      <Card title="会话元数据">
        <Empty description="无回放数据" />
      </Card>
    )
  }

  return (
    <Card title="会话元数据" size="small">
      <Descriptions
        column={{ xs: 1, sm: 2, md: 3 }}
        size="small"
        bordered
        items={[
          {
            key: 'world_id',
            label: 'World ID',
            children: <code className="text-xs">{session.world_id}</code>,
          },
          {
            key: 'player',
            label: '玩家',
            // player_name 来自 ai-engine 写入的 snapshot.player_name;
            // ai-engine 若未取到 player_prior_profile.name 则写 None, 此处用 player_id 占位.
            children: session.player_name ? (
              session.player_name
            ) : (
              <span>
                <code className="text-xs">{session.player_id}</code>
                <Tooltip title="ai-engine 未上报 player_name (player_prior_profile 为空或缺失), 暂用 player_id 占位">
                  <InfoCircleOutlined style={{ marginLeft: 4, color: '#bfbfbf', fontSize: 11 }} />
                </Tooltip>
              </span>
            ),
          },
          {
            key: 'character',
            label: '角色',
            children: session.character_name ?? '-',
          },
          {
            key: 'status',
            label: '状态',
            children: (
              <Tag color={STATUS_COLOR[session.status] ?? 'default'}>
                {session.status}
              </Tag>
            ),
          },
          {
            key: 'ending',
            label: '结局',
            children: session.ending_type ? (
              <Tag color={ENDING_COLOR[session.ending_type] ?? 'default'}>
                {session.ending_name || session.ending_type}
              </Tag>
            ) : (
              '-'
            ),
          },
          {
            key: 'score',
            label: '得分/排名',
            children:
              session.score == null
                ? NA
                : `${session.score}${session.rank != null ? ` / #${session.rank}` : ''}`,
          },
          {
            key: 'started_at',
            label: '开始时间',
            children: dayjs(session.started_at).format('YYYY-MM-DD HH:mm:ss'),
          },
          {
            key: 'ended_at',
            label: '结束时间',
            children: session.ended_at
              ? dayjs(session.ended_at).format('YYYY-MM-DD HH:mm:ss')
              : '未结束',
          },
        ]}
      />
    </Card>
  )
}

export default SessionMetadata